import {
  Autocomplete,
  Box,
  createFilterOptions,
  SxProps,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

// A dedicated component to display a tooltip only when the content overflows.
interface TooltipOnOverflowProps {
  title: string;
  children: React.ReactNode;
}

const TooltipOnOverflow = (props: TooltipOnOverflowProps) => {
  const { title, children } = props;
  const [isOverflowing, setIsOverflowing] = useState(false);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget;
    setIsOverflowing(el.scrollWidth > el.clientWidth);
  };

  return (
    <Tooltip title={title} disableHoverListener={!isOverflowing}>
      <Box
        onMouseEnter={handleMouseEnter}
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: "100%",
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

interface FormAutocompleteProps {
  name: string;
  label: string;
  options: { id: number; name: string; code?: string | null; unit?: string | null }[];
  isEditing: boolean;
  isSaving: boolean;
  disabled?: boolean;
  formStyles: SxProps;
}

function FormAutocomplete({
  name,
  label,
  options,
  isEditing,
  isSaving,
  disabled = false,
  formStyles,
}: Readonly<FormAutocompleteProps>) {
  const { control } = useFormContext();
  const { t } = useTranslation();

  const [showInputTooltip, setShowInputTooltip] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Creates a Map from option IDs to option objects for O(1) lookup
  const optionsMap = useMemo(() => {
    const map = new Map<number, { id: number; name: string; code?: string | null; unit?: string | null }>();
    options.forEach(opt => map.set(opt.id, opt));
    return map;
  }, [options]);

  const getLabel = (option: { id: number; name: string; code?: string | null; unit?: string | null }) =>
    option.code ? `(${option.code}) ${option.name}` : option.name;

  const filterOptions = createFilterOptions({
    stringify: (option: { id: number; name: string; code?: string | null; unit?: string | null }) =>
      `${getLabel(option)} ${option.unit ?? ""}`,
  });

  return (
    <Controller
      name={name as any}
      control={control}
      render={({ field, fieldState }) => {
        const selectedOption = optionsMap.get(field.value) ?? null;
        const selectedLabel = selectedOption
          ? getLabel(selectedOption)
          : "";

        return (
          <Autocomplete
            fullWidth
            componentsProps={{
              paper: {
                sx: { minWidth: "fit-content", whiteSpace: "nowrap" }
              }
            }}
            options={options}
            value={selectedOption}
            getOptionLabel={getLabel}
            filterOptions={filterOptions}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, newValue) => field.onChange(newValue ? newValue.id : null)}
            disabled={!isEditing || isSaving || disabled || options.length === 0}
            autoHighlight
            noOptionsText={t("generic.no-options")}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              const label = getLabel(option);

              return (
                <li key={option.id} {...rest}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 1 }}>
                    <TooltipOnOverflow title={label}>{label}</TooltipOnOverflow>
                    {option.unit && (
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {option.unit}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <Tooltip
                title={showInputTooltip ? selectedLabel : ""}
                disableHoverListener={!showInputTooltip}
              >
                <TextField
                  {...params}
                  label={label}
                  variant={isEditing ? "outlined" : "standard"}
                  sx={{ ...formStyles }}
                  inputRef={inputRef}
                  onMouseEnter={() => {
                    const input = inputRef.current;
                    if (input) {
                      setShowInputTooltip(input.scrollWidth > input.clientWidth);
                    }
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              </Tooltip>
            )}
          />
        );
      }}
    />
  );
}

export default FormAutocomplete;
